import { useEffect, useMemo, useState } from "react";
import {
  AppBar,
  Toolbar,
  Box,
  Typography,
  Button,
  IconButton,
  Drawer,
  Stack,
  Container,
  Divider,
} from "@mui/material";

import {
  FaMusic,
  FaTheaterMasks,
  FaLaughBeam,
  FaChalkboardTeacher,
  FaUsers,
  FaTools,
  FaBriefcase,
  FaGlassCheers,
  FaGhost,
  FaHeadphonesAlt,
  FaFutbol,
  FaRunning,
  FaPalette,
  FaUtensils,
  FaChild,
  FaVrCardboard,
  FaPrayingHands,
  FaGamepad,
  FaMapMarkedAlt,
} from "react-icons/fa";

import CelebrationIcon from "@mui/icons-material/Celebration";
import EventIcon from "@mui/icons-material/Event";

import Avatar from "@mui/material/Avatar";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";

import MenuIcon from "@mui/icons-material/Menu";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import { Link as RouterLink, useNavigate } from "react-router-dom";

import { motion, AnimatePresence } from "framer-motion";

import Logo from "../../assets/logos/icone_branco.png";

import { useAuthContext } from "../../contexts/AuthContext";

// Firebase
import { rtdb } from "../../services/firebase/config";
import { ref as rRef, get as rGet } from "firebase/database";

export default function Header() {
  const { user, profile, role, approvalStatus, logout } = useAuthContext();

  // Helpers de estado
  const isOrganizerApproved = role === "organizer" && approvalStatus === "approved";
  const isOrganizerPending  = role === "organizer" && approvalStatus === "pending";
  const displayName = profile?.name || user?.displayName || "Usuario";
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);

  const [anchorEl, setAnchorEl] = useState(null);
  const openMenu = Boolean(anchorEl);

  // -----------------------------
  // AVATAR ICONS (mesmo padrão do MyProfile)
  // -----------------------------
  const AVATAR_ICONS = useMemo(
    () => ({
      music: FaMusic,
      theater: FaTheaterMasks,
      laugh: FaLaughBeam,
      teacher: FaChalkboardTeacher,
      users: FaUsers,
      tools: FaTools,
      briefcase: FaBriefcase,
      party: FaGlassCheers,
      ghost: FaGhost,
      headphones: FaHeadphonesAlt,
      soccer: FaFutbol,
      running: FaRunning,
      palette: FaPalette,
      food: FaUtensils,
      child: FaChild,
      vr: FaVrCardboard,
      pray: FaPrayingHands,
      game: FaGamepad,
      map: FaMapMarkedAlt,
      celebration: CelebrationIcon,
      event: EventIcon,
    }),
    [],
  );

  const DEFAULT_AVATAR = "users";

  const [avatarIcon, setAvatarIcon] = useState(DEFAULT_AVATAR);

  // Carrega avatarIcon salvo (prioridade RTDB, fallback Firestore)
  useEffect(() => {
    if (!user?.uid) {
      setAvatarIcon(DEFAULT_AVATAR);
      return;
    }

    let alive = true;

    (async () => {
      try {
        // 1) RTDB primeiro (realtime/soberano)
        const profileRef = rRef(rtdb, `users/${user.uid}`);
        const rSnap = await rGet(profileRef);
        if (alive && rSnap.exists()) {
          const p = rSnap.val();
          if (p?.avatarIcon) {
            setAvatarIcon(p.avatarIcon);
            return;
          }
        }

        // 2) fallback RTDB users/{uid}
        const userRef = rRef(rtdb, `users/${user.uid}`);
        const fSnap = await rGet(userRef);
        if (alive && fSnap.exists()) {
          const d = fSnap.val() || {};
          setAvatarIcon(d.avatarIcon || DEFAULT_AVATAR);
        } else if (alive) {
          setAvatarIcon(DEFAULT_AVATAR);
        }
      } catch (err) {
        // fallback silencioso
        if (alive) setAvatarIcon(DEFAULT_AVATAR);
      }
    })();

    return () => {
      alive = false;
    };
  }, [user?.uid]);

  const AvatarIconComponent = AVATAR_ICONS[avatarIcon] || FaUsers;

  const closeMenu = () => setAnchorEl(null);

  const handleMenuGo = (path) => {
    closeMenu();
    navigate(path);
  };

  const handleLogout = async () => {
    closeMenu();
    try {
      await logout?.();
    } finally {
      navigate("/");
    }
  };

  const toggleDrawer = (value) => () => setOpen(value);

  const drawerLinkStyle = {
    width: "100%",
    alignItems: "left",
    display: "flex",
    justifyContent: "flex-start",
    color: "#fff",
    fontWeight: 700,
    fontSize: 15,
    px: 2,
    py: 2,
    borderRadius: 2,
    textTransform: "none",
    background: "rgba(255,255,255,0.04)",
    border: "1px solid rgba(255,255,255,0.08)",
    "&:hover": {
      background: "rgba(255,255,255,0.08)",
      transform: "translateX(2px)",
    },
  };

  const drawerVariants = {
    hidden: { x: "100%", opacity: 0 },
    visible: {
      x: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 260,
        damping: 28,
        when: "beforeChildren",
        staggerChildren: 0.08,
      },
    },
    exit: {
      x: "100%",
      opacity: 0,
      transition: { duration: 0.25, ease: "easeInOut" },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, x: 20 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.35, ease: "easeOut" },
    },
  };

  const headerVariants = {
    hidden: { opacity: 0, y: -12 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.35, ease: "easeOut" },
    },
  };

  const NavLinks = ({ mobile = false }) => (
    <Stack
      direction={mobile ? "column" : "row"}
      spacing={mobile ? 2 : 3}
      alignItems={mobile ? "flex-start" : "center"}
      sx={{ width: mobile ? "100%" : "auto" }}
    >
      <Button
        component={RouterLink}
        to="/eventos"
        color="inherit"
        sx={{ fontWeight: 600 }}
        onClick={mobile ? toggleDrawer(false) : undefined}
      >
        Eventos
      </Button>

      <Button
        component={RouterLink}
        to="/organizadores"
        color="inherit"
        sx={{ fontWeight: 600 }}
        onClick={mobile ? toggleDrawer(false) : undefined}
      >
        Para Organizadores
      </Button>
    </Stack>
  );

  return (
    <>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          background: "rgba(8, 22, 43, 0.86)",
          backdropFilter: "blur(10px)",
          borderBottom: "1px solid var(--linha)",
        }}
      >
        <Container
          maxWidth={false}
          sx={{
            maxWidth: "1120px",
            px: { xs: 2, sm: 2.5 },
          }}
        >
          <Toolbar
            disableGutters
            sx={{
              minHeight: 72,
              display: "flex",
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            {/* BRAND */}
            <Box
              component={RouterLink}
              to="/"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                textDecoration: "none",
              }}
            >
              <Box
                component="img"
                src={Logo}
                alt="ONURB PASS"
                sx={{ height: 38 }}
              />
              <Typography variant="h6" sx={{ fontWeight: 800, color: "#fff" }}>
                ONURB PASS
              </Typography>
            </Box>

            {/* DESKTOP NAV */}
            <Box sx={{ display: { xs: "none", md: "flex" } }}>
              <NavLinks />
            </Box>

            {/* CTA DESKTOP */}
            <Box
              sx={{
                display: { xs: "none", md: "flex" },
                gap: 1.5,
                alignItems: "center",
              }}
            >
              {/* NAO LOGADO */}
              {!user && (
                <>
                  <Button
                    component={RouterLink}
                    to="/eventos"
                    sx={{
                      px: 2,
                      py: 1.2,
                      borderRadius: 1,
                      border: "1px solid rgba(255,255,255,0.14)",
                      background: "rgba(255,255,255,0.04)",
                      color: "#fff",
                      fontWeight: 800,
                      "&:hover": { background: "rgba(255,255,255,0.08)" },
                    }}
                  >
                    Comprar ingressos
                  </Button>
                  <Button
                    component={RouterLink}
                    to="/login"
                    sx={{
                      px: 2.2,
                      py: 1.2,
                      borderRadius: 1,
                      fontWeight: 900,
                      color: "#fff",
                      border: "1px solid rgba(255,255,255,0.22)",
                      background: "transparent",
                      "&:hover": { background: "rgba(255,255,255,0.06)" },
                    }}
                  >
                    Entrar
                  </Button>
                </>
              )}

              {/* ORGANIZER PENDING */}
              {isOrganizerPending && (
                <Button
                  component={RouterLink}
                  to="/organizadores/solicitacao-enviada"
                  sx={{
                    px: 2,
                    py: 1.2,
                    borderRadius: 1,
                    border: "1px solid rgba(255,193,7,0.35)",
                    background: "rgba(255,193,7,0.08)",
                    color: "#ffd54f",
                    fontWeight: 800,
                    fontSize: 12,
                  }}
                >
                  Em analise
                </Button>
              )}

              {/* ORGANIZER APPROVED: acesso ao dashboard */}
              {isOrganizerApproved && (
                <Button
                  component={RouterLink}
                  to="/dashboard"
                  sx={{
                    px: 2,
                    py: 1.2,
                    borderRadius: 1,
                    border: "1px solid rgba(26,255,122,0.30)",
                    background: "rgba(26,255,122,0.08)",
                    color: "var(--verdeClaro)",
                    fontWeight: 900,
                  }}
                >
                  Dashboard
                </Button>
              )}

              {/* CUSTOMER: meus ingressos */}
              {user && role === "customer" && (
                <Button
                  component={RouterLink}
                  to="/meus-ingressos"
                  startIcon={<PersonOutlineIcon />}
                  sx={{
                    px: 2,
                    py: 1.2,
                    borderRadius: 1,
                    border: "1px solid rgba(255,255,255,0.14)",
                    background: "rgba(255,255,255,0.04)",
                    color: "#fff",
                    fontWeight: 800,
                    "&:hover": { background: "rgba(255,255,255,0.08)" },
                  }}
                >
                  Meus ingressos
                </Button>
              )}

              {/* LOGADO: avatar menu */}
              {user && (
                <>
                  <Button
                    onClick={(e) => setAnchorEl(e.currentTarget)}
                    sx={{
                      pl: 1,
                      pr: 1.5,
                      py: 0.8,
                      borderRadius: 1,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      background: "rgba(255,255,255,0.06)",
                      border: "1px solid rgba(255,255,255,0.14)",
                      color: "#fff",
                      textTransform: "none",
                      "&:hover": { background: "rgba(255,255,255,0.1)" },
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 32,
                        height: 32,
                        bgcolor: "rgba(255,255,255,0.06)",
                        border: "1px solid rgba(255,255,255,0.14)",
                        color: "var(--verdeClaro)",
                      }}
                    >
                      <Box sx={{ fontSize: 16, display: "grid", placeItems: "center" }}>
                        <AvatarIconComponent />
                      </Box>
                    </Avatar>
                    <Box sx={{ textAlign: "left" }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 800, lineHeight: 1 }}>
                        {displayName.split(" ")[0]}
                      </Typography>
                    </Box>
                  </Button>

                  <Menu
                    anchorEl={anchorEl}
                    open={openMenu}
                    onClose={closeMenu}
                    PaperProps={{
                      sx: {
                        mt: 1,
                        background: "rgba(8,22,43,0.96)",
                        backdropFilter: "blur(12px)",
                        border: "1px solid rgba(255,255,255,0.12)",
                        color: "#fff",
                        minWidth: 190,
                      },
                    }}
                  >
                    {role === "customer" && (
                      <MenuItem onClick={() => handleMenuGo("/meus-ingressos")}>
                        Meus ingressos
                      </MenuItem>
                    )}
                    {isOrganizerApproved && (
                      <MenuItem onClick={() => handleMenuGo("/dashboard")}>
                        Dashboard
                      </MenuItem>
                    )}
                    {isOrganizerPending && (
                      <MenuItem onClick={() => handleMenuGo("/organizadores/solicitacao-enviada")}
                        sx={{ color: "#ffd54f" }}>
                        Solicitacao em analise
                      </MenuItem>
                    )}
                    <MenuItem onClick={() => handleMenuGo("/perfil")}>
                      Meu perfil
                    </MenuItem>
                    <Divider sx={{ borderColor: "rgba(255,255,255,0.10)" }} />
                    <MenuItem onClick={handleLogout} sx={{ color: "var(--vermelhoClaro)" }}>
                      Sair
                    </MenuItem>
                  </Menu>
                </>
              )}
            </Box>

            {/* MOBILE MENU BUTTON */}
            <IconButton
              onClick={toggleDrawer(true)}
              sx={{ display: { xs: "flex", md: "none" }, color: "#fff" }}
            >
              <MenuIcon />
            </IconButton>
          </Toolbar>
        </Container>
      </AppBar>

      {/* MOBILE DRAWER */}
      <AnimatePresence>
        {open && (
          <Drawer
            anchor="right"
            open={open}
            onClose={toggleDrawer(false)}
            PaperProps={{
              component: motion.div,
              variants: drawerVariants,
              initial: "hidden",
              animate: "visible",
              exit: "exit",
              sx: {
                width: "100%",
                background: "rgba(8,22,43,0.92)",
                backdropFilter: "blur(14px)",
                borderLeft: "1px solid rgba(255,255,255,0.08)",
              },
            }}
          >
            <Box
              sx={{ height: "100%", display: "flex", flexDirection: "column" }}
            >
              {/* HEADER */}
              <motion.div variants={headerVariants}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    px: 2.5,
                    py: 2,
                    borderBottom: "1px solid rgba(255,255,255,0.08)",
                  }}
                >
                  <Stack direction="row" spacing={1.2} alignItems="center">
                    <Box
                      component="img"
                      src={Logo}
                      alt="ONURB PASS"
                      sx={{ height: 28 }}
                    />
                    <Typography sx={{ fontWeight: 900, color: "#fff" }}>
                      ONURB PASS
                    </Typography>
                  </Stack>

                  <IconButton
                    onClick={toggleDrawer(false)}
                    sx={{ color: "#fff" }}
                  >
                    ✕
                  </IconButton>
                </Box>
              </motion.div>

              {/* USER CARD (MOBILE) */}
              {user && (
                <Box sx={{ px: 2.5, pt: 2.5 }}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      border: "1px solid rgba(255,255,255,0.10)",
                      background: "rgba(255,255,255,0.04)",
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 44,
                        height: 44,
                        bgcolor: "rgba(255,255,255,0.06)",
                        border: "1px solid rgba(255,255,255,0.14)",
                        color: "var(--verdeClaro)",
                      }}
                    >
                      <Box
                        sx={{
                          fontSize: 22,
                          display: "grid",
                          placeItems: "center",
                        }}
                      >
                        <AvatarIconComponent />
                      </Box>
                    </Avatar>

                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ color: "#fff", fontWeight: 900, lineHeight: 1.1 }}>
                        {displayName}
                      </Typography>
                      <Typography sx={{ color: "rgba(255,255,255,0.70)", fontSize: 12 }}>
                        {user.email}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}

              {/* NAV */}
              <Stack spacing={1.5} sx={{ px: 2.5, py: 3, flex: 1 }}>
                <motion.div variants={itemVariants}>
                  <Button
                    component={RouterLink}
                    to="/eventos"
                    onClick={toggleDrawer(false)}
                    sx={drawerLinkStyle}
                  >
                    Eventos
                  </Button>
                </motion.div>

                <motion.div variants={itemVariants}>
                  <Button
                    component={RouterLink}
                    to="/organizadores"
                    onClick={toggleDrawer(false)}
                    sx={drawerLinkStyle}
                  >
                    Para Organizadores
                  </Button>
                </motion.div>

                {/* CTA principal mobile */}
                <motion.div variants={itemVariants}>
                  <Button
                    component={RouterLink}
                    to={user ? (isOrganizerApproved ? "/dashboard" : "/meus-ingressos") : "/eventos"}
                    fullWidth
                    onClick={toggleDrawer(false)}
                    sx={{
                      py: 2,
                      display: "flex",
                      justifyContent: "flex-start",
                      borderRadius: 2,
                      fontWeight: 900,
                      background: "linear-gradient(135deg, var(--verdeClaro), #6dffb2)",
                      color: "#042014",
                      boxShadow: "0 8px 30px rgba(0,255,170,0.35)",
                    }}
                  >
                    {!user && "Comprar ingressos"}
                    {user && role === "customer" && "Meus ingressos"}
                    {isOrganizerApproved && "Dashboard"}
                    {isOrganizerPending && "Solicitacao em analise"}
                  </Button>
                </motion.div>

                {/* MOBILE: organizer pending badge */}
                {isOrganizerPending && (
                  <motion.div variants={itemVariants}>
                    <Button
                      component={RouterLink}
                      to="/organizadores/solicitacao-enviada"
                      onClick={toggleDrawer(false)}
                      sx={{ ...drawerLinkStyle, color: "#ffd54f", borderColor: "rgba(255,193,7,0.25)" }}
                    >
                      Ver status da solicitacao
                    </Button>
                  </motion.div>
                )}

                {/* MOBILE: MEU PERFIL (logado) */}
                {user && (
                  <motion.div variants={itemVariants}>
                    <Button
                      onClick={() => { toggleDrawer(false)(); navigate("/perfil"); }}
                      sx={drawerLinkStyle}
                    >
                      Meu perfil
                    </Button>
                  </motion.div>
                )}

                {/* MOBILE: ENTRAR (nao logado) */}
                {!user && (
                  <motion.div variants={itemVariants}>
                    <Button
                      component={RouterLink}
                      to="/login"
                      onClick={toggleDrawer(false)}
                      sx={{ ...drawerLinkStyle, background: "transparent", border: "1px solid rgba(255,255,255,0.22)", fontWeight: 900 }}
                    >
                      Entrar
                    </Button>
                  </motion.div>
                )}

                {/* MOBILE: SAIR (logado) */}
                {user && (
                  <motion.div variants={itemVariants}>
                    <Button
                      onClick={async () => { toggleDrawer(false)(); await logout?.(); navigate("/"); }}
                      sx={{ ...drawerLinkStyle, background: "rgba(255,60,60,0.12)", border: "1px solid rgba(255,60,60,0.25)", color: "#ffb3b3", fontWeight: 900 }}
                    >
                      Sair
                    </Button>
                  </motion.div>
                )}
              </Stack>
            </Box>
          </Drawer>
        )}
      </AnimatePresence>
    </>
  );
}
